import { useEffect, useRef } from "react";
import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Stethoscope,
  Users2,
  Truck,
  ArrowUpRight,
  Check,
  X,
} from "lucide-react";

const SOLUTIONS = [
  {
    slug: "dental-labs",
    icon: Building2,
    title: "Dental Labs running on Rxn3D.",
    tag: "For labs of every size",
    desc: "Digital-first labs that ship cases faster, communicate more clearly, and get paid on time.",
    highlights: ["Live queue", "Auto-invoicing", "Proof of delivery", "Structured stages"],
    before: [
      "Paper Rx and email threads slow every case",
      "Doctors can't see status without calling",
      "Invoicing is manual and error-prone",
      "Deliveries lack a verifiable trail",
    ],
    with: [
      "One live queue of every case, filterable and searchable",
      "Auto-invoice from the Rx, reconcile with delivery",
      "Signed, timestamped proof for every drop-off",
      "Structured production stages tuned to your lab",
    ],
    builtFor: [
      {
        title: "Technicians",
        text: "One queue, clean handoffs, no repeated questions from the front desk.",
      },
      {
        title: "Managers",
        text: "Real-time visibility into production, delivery, and revenue, without chasing anyone for a status update.",
      },
    ],
    faq: [
      {
        q: "Can we migrate from paper today?",
        a: "Yes. Rxn3D is designed for progressive adoption. Start with a few doctors and expand.",
      },
      {
        q: "How is production customized to my lab?",
        a: "You define stages, stations and roles. We help you model your operation in onboarding.",
      },
      {
        q: "What about existing invoices?",
        a: "You can continue existing systems while Rxn3D handles new cases, or migrate. Our team helps either way.",
      },
      {
        q: "Is there a doctor app?",
        a: "Doctors work in-browser or mobile web. A native app is on the roadmap.",
      },
    ],
  },
  {
    slug: "dental-offices",
    icon: Stethoscope,
    title: "Dental Offices with total visibility.",
    tag: "For clinics & practices",
    desc: "Create an Rx in under a minute and watch every stage. No more phone tag and no more surprises.",
    highlights: ["Structured Rx", "Live status", "Notifications", "Doctor signature"],
    before: [
      "Long calls to check case status",
      "Missed handoffs between doctors and lab",
      "Delivery ambiguity on days when appointments matter",
    ],
    with: [
      "Structured Rx entry with tooth chart and shade",
      "Real-time status and delivery ETA",
      "Automated notifications per event",
      "Doctor signature captured digitally",
    ],
    faq: [
      {
        q: "Can multiple doctors share one clinic?",
        a: "Yes. Every doctor has their own workspace under a shared clinic account.",
      },
      {
        q: "How do notifications work?",
        a: "In-app, email and SMS per event: new case, status change, delivery, invoice.",
      },
      {
        q: "Where can I see the full doctor feature list?",
        a: "Visit the For doctors page for tooth-by-tooth prescribing, digital slips, live tracking, practice roles, and more.",
      },
    ],
  },
  {
    slug: "multi-location-access",
    icon: Users2,
    title: "Work across every location, one account",
    tag: "For multi-location chains",
    desc: "Get invited into as many dental offices or labs as you need. Switch between them without separate logins.",
    highlights: ["Single account", "Fast switching", "Role-based access", "Office + lab ready"],
    before: [
      "Juggling separate logins for every location you work at",
      "Re-entering the same credentials for every office or lab",
      "No easy way to move between the locations you're part of",
    ],
    with: [
      "One account across every location you're invited to",
      "Switch locations instantly from a dropdown",
      "Role-based permissions set per location by that location's admin",
      "Works whether you're at a dental office, a lab, or both",
    ],
  },
  {
    slug: "drivers",
    icon: Truck,
    title: "Drivers with routes that make sense.",
    tag: "For pickup & delivery teams",
    desc: "Built for pickup and delivery teams. Get optimized routes, ETAs, signatures, and proof photos.",
    highlights: ["Optimized routes", "Signatures + photos", "Live ETAs", "Timestamped trail"],
    before: [
      "Manual routes waste hours per driver every week",
      "Deliveries without proof are hard to reconcile",
      "Dispatchers have no live view",
    ],
    with: [
      "Optimized daily routes, built mobile-first",
      "Signature and photo capture on delivery",
      "Live dispatcher view with ETAs",
      "A full timestamped trail for every stop",
    ],
    faq: [
      {
        q: "Does it work on iOS and Android?",
        a: "Yes. Drivers use a mobile-first web app, installable as a PWA.",
      },
      {
        q: "Can dispatchers see live?",
        a: "Yes. Live positions and ETAs update as drivers move.",
      },
    ],
  },
];
const VALID_SOLUTION_SLUGS = SOLUTIONS.map((s) => s.slug);

export default function Solutions() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const didInitialSync = useRef(false);
  const cardRefs = useRef({});
  const activeSlug = VALID_SOLUTION_SLUGS.includes(slug) ? slug : null;

  useEffect(() => {
    if (!slug) return;
    if (slug === "dental-groups") {
      navigate("/solutions/multi-location-access", { replace: true });
      return;
    }
    if (!VALID_SOLUTION_SLUGS.includes(slug)) {
      navigate("/solutions", { replace: true });
    }
  }, [slug, navigate]);

  useEffect(() => {
    if (!activeSlug) return;
    const el = cardRefs.current[activeSlug];
    if (!el) return;

    const top = el.getBoundingClientRect().top + window.scrollY;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const offset = isMobile ? 96 : 72;

    requestAnimationFrame(() => {
      window.scrollTo({
        top: Math.max(top - offset, 0),
        behavior: didInitialSync.current ? "smooth" : "auto",
      });
      didInitialSync.current = true;
    });
  }, [activeSlug, location.pathname]);

  const onToggle = (nextSlug) => {
    if (activeSlug === nextSlug) {
      navigate("/solutions");
      return;
    }
    navigate(`/solutions/${nextSlug}`);
  };

  return (
    <>
      <PageHero
        chapter="§ 04"
        eyebrow="Solutions"
        title={"Built for the people who run dental."}
        description="Everyone around the case gets a workspace built for their job, not a one-size-fits-all tool."
      />

      <section data-testid="solutions-grid" className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {SOLUTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.slug}
                data-testid={`solution-card-${s.slug}`}
                ref={(node) => {
                  cardRefs.current[s.slug] = node;
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                whileHover={{ y: -4 }}
                className="group rounded-3xl hairline glass p-8 lg:p-10 relative overflow-hidden scroll-mt-28"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute -inset-6 bg-brand-radial blur-2xl opacity-40" />
                </div>
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="h-12 w-12 rounded-xl hairline inline-flex items-center justify-center">
                      <Icon className="h-5 w-5 text-brand-plum" />
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                      Solution · {i + 1}
                    </div>
                  </div>
                  <div className="mt-8">
                    <div className="text-xs text-muted-foreground">{s.tag}</div>
                    <h3 className="mt-2 font-heading text-3xl lg:text-4xl font-semibold tracking-[-0.02em]">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-lg leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                  <div className="mt-8 flex flex-wrap gap-2">
                    {s.highlights.map((h) => (
                      <span
                        key={h}
                        className="text-xs px-3 py-1.5 rounded-full hairline"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => onToggle(s.slug)}
                      data-testid={`solution-toggle-${s.slug}`}
                      aria-expanded={activeSlug === s.slug}
                      aria-controls={`solution-content-${s.slug}`}
                      className="w-full sm:w-auto inline-flex items-center justify-between sm:justify-center gap-2 text-sm font-semibold rounded-xl hairline px-4 py-3 hover:bg-secondary/60 transition-colors"
                    >
                      <span>
                        {activeSlug === s.slug ? `Collapse ${s.title}` : `Explore ${s.title}`}
                      </span>
                      {activeSlug === s.slug ? (
                        <X className="h-4 w-4" />
                      ) : (
                        <ArrowUpRight className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  {activeSlug === s.slug && (
                    <div
                      id={`solution-content-${s.slug}`}
                      className="mt-8 border-t border-border/60 pt-6 space-y-6"
                    >
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        <div className="rounded-2xl hairline p-5">
                          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                            Before Rxn3D
                          </div>
                          <ul className="mt-4 space-y-2.5">
                            {s.before.map((item) => (
                              <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed">
                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/40" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="rounded-2xl bg-brand-ink text-white p-5">
                          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/70">
                            With Rxn3Ds
                          </div>
                          <ul className="mt-4 space-y-2.5">
                            {s.with.map((item) => (
                              <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed">
                                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                                <span className="text-white/90">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {s.builtFor && (
                        <div className="rounded-2xl hairline p-5">
                          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                            Built for the whole lab
                          </div>
                          <ul className="mt-4 space-y-2.5">
                            {s.builtFor.map((item) => (
                              <li key={item.title} className="text-sm leading-relaxed">
                                <span className="font-semibold">{item.title}:</span> {item.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {s.faq && (
                        <div className="rounded-2xl hairline p-5">
                          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">
                            FAQ
                          </div>
                          <Accordion type="single" collapsible className="w-full">
                            {s.faq.map((it, faqIndex) => (
                              <AccordionItem key={it.q} value={`${s.slug}-faq-${faqIndex}`}>
                                <AccordionTrigger className="text-left text-sm hover:no-underline">
                                  {it.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                                  {it.a}
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      <CTA />
    </>
  );
}
